import { LitElement, html, ifDefined } from '@lit'

/**
 * Lit component for rendering responsive images in JS context, inspired by Hydrogen Image component.
 * It supports the same props as the HTML `img` element, but automatically
 * generates the srcSet and sizes attributes for you. For most use cases,
 * you'll want to set the `aspectRatio` prop to ensure the image is sized
 * correctly.
 */
export class DNAImage extends LitElement {
  static get properties() {
    return {
      alt: { type: String },
      aspectRatio: { type: String, attribute: 'aspect-ratio' },
      crop: { type: String },
      data: { type: Object },
      decoding: { type: String },
      height: { type: String },
      loader: { type: Object, attribute: false },
      loading: { type: String },
      fetchPriority: { type: String, attribute: 'fetch-priority' },
      sizes: { type: String },
      src: { type: String },
      srcSetOptions: { type: Object },
      width: { type: String },
    }
  }

  constructor() {
    super()
    this.alt = ''
    this.aspectRatio = undefined
    this.crop = false
    this.data = undefined
    this.decoding = 'async'
    this.height = 'auto'
    this.loader = shopifyLoader
    this.loading = 'lazy'
    this.fetchPriority = 'low'
    this.sizes = undefined
    this.src = undefined
    this.srcSetOptions = {
      intervals: 7,
      startingWidth: 200,
      incrementSize: 300,
      placeholderWidth: 100,
    }
    this.width = '100%'
  }

  render() {
    const normalizedProps = this._getNormalizedProps()
    const { intervals, startingWidth, incrementSize, placeholderWidth } =
      this.srcSetOptions
    const imageWidths = generateImageWidths(
      this.width,
      intervals,
      startingWidth,
      incrementSize,
    )
    const fixedWidth = isFixedWidth(normalizedProps.width)

    if (!this.sizes && !fixedWidth) {
      console.warn(
        [
          'No sizes prop provided to Image component,',
          'you may be loading unnecessarily large images.',
          `Image used is ${this.src || this.data?.url || 'unknown'}`,
        ].join(' '),
      )
    }

    if (fixedWidth) {
      return html`
        <dna-fixed-width-image
          aspectRatio=${this.aspectRatio}
          crop=${this.crop}
          decoding=${this.decoding}
          height=${this.height}
          .imageWidths=${imageWidths}
          .loader=${this.loader}
          loading=${this.loading}
          fetch-priority=${this.fetchPriority}
          .normalizedProps=${normalizedProps}
          width=${this.width}
          part="base"
        ></dna-fixed-width-image>
      `
    }
    return html`
        <dna-fluid-image
          aspectRatio=${this.aspectRatio}
          crop=${this.crop}
          decoding=${this.decoding}
          .imageWidths=${imageWidths}
          .loader=${this.loader}
          loading=${this.loading}
          fetch-priority=${this.fetchPriority}
          .normalizedProps=${normalizedProps}
          placeholderWidth=${placeholderWidth}
          sizes=${this.sizes}
          part="base"
        ></dna-fluid-image>
      `
  }

  _getNormalizedData() {
    const dataWidth =
      this.data?.width && this.data?.height ? this.data?.width : undefined

    const dataHeight =
      this.data?.width && this.data?.height ? this.data?.height : undefined

    return {
      ...this.data,
      width: dataWidth,
      height: dataHeight,
      unitsMatch: Boolean(unitsMatch(dataWidth, dataHeight)),
    }
  }

  _getNormalizedProps() {
    const normalizedData = this._getNormalizedData()
    const nWidthProp = this.width || '100%'
    const widthParts = getUnitValueParts(nWidthProp.toString())
    const nWidth = `${widthParts.number}${widthParts.unit}`

    const autoHeight =
      this.height === undefined ||
      this.height === null ||
      this.height === 'auto'
    const heightParts = autoHeight
      ? null
      : getUnitValueParts(this.height.toString())
    const fixedHeight = heightParts
      ? `${heightParts.number}${heightParts.unit}`
      : ''
    const nHeight = autoHeight ? 'auto' : fixedHeight

    const nSrc = this.src || normalizedData?.url

    if (!nSrc) {
      console.warn('No src or data.url provided to Image component.')
    }

    const nAlt =
      normalizedData?.altText && !this.alt
        ? normalizedData?.altText
        : this.alt || ''

    const nAspectRatio = this.aspectRatio
      ? this.aspectRatio
      : this._getNormalizedData().unitsMatch
        ? [
            getNormalizedFixedUnit(this._getNormalizedData().width),
            getNormalizedFixedUnit(this._getNormalizedData().height),
          ].join('/')
        : undefined

    return {
      width: nWidth,
      height: nHeight,
      src: nSrc,
      alt: nAlt,
      aspectRatio: nAspectRatio,
    }
  }
}

window.customElements.define('dna-image', DNAImage)

export class DNAFixedWidthImage extends LitElement {
  static get properties() {
    return {
      aspectRatio: { type: String },
      crop: { type: String },
      decoding: { type: String },
      height: { type: String },
      imageWidths: { type: Array },
      loader: { type: Object, attribute: false },
      loading: { type: String },
      fetchPriority: { type: String, attribute: 'fetch-priority' },
      normalizedProps: { type: Object },
      width: { type: String },
    }
  }

  constructor() {
    super()
    this.aspectRatio = undefined
    this.crop = false
    this.decoding = 'async'
    this.height = 'auto'
    this.imageWidths = []
    this.loader = undefined
    this.loading = 'lazy'
    this.fetchPriority = 'low'
    this.normalizedProps = {}
    this.width = '100%'
  }

  render() {
    const {
      aspectRatio,
      crop,
      decoding,
      height,
      imageWidths,
      loader,
      loading,
      fetchPriority,
      normalizedProps,
      width,
    } = this

    const intWidth = getNormalizedFixedUnit(width)
    const intHeight = getNormalizedFixedUnit(height)

    const fixedAspectRatio = aspectRatio
      ? aspectRatio
      : unitsMatch(normalizedProps.width, normalizedProps.height)
        ? [intWidth, intHeight].join('/')
        : normalizedProps.aspectRatio
          ? normalizedProps.aspectRatio
          : undefined

    const sizesArray =
      imageWidths === undefined
        ? undefined
        : generateSizes(imageWidths, fixedAspectRatio, crop)

    const parsedAspectRatio = parseAspectRatio(fixedAspectRatio)

    const fixedHeight = intHeight
      ? intHeight
      : fixedAspectRatio && intWidth && !Number.isNaN(parsedAspectRatio)
        ? intWidth * (parsedAspectRatio ?? 1)
        : undefined

    const srcSet = generateSrcSet(normalizedProps.src, sizesArray, loader)
    const src = loader({
      src: normalizedProps.src,
      width: intWidth,
      height: fixedHeight,
      crop: normalizedProps.height === 'auto' ? undefined : crop,
    })

    return html`
      <img
        alt=${normalizedProps.alt}
        decoding=${decoding}
        height=${ifDefined(fixedHeight)}
        loading=${loading}
        fetchpriority=${fetchPriority}
        src=${src}
        srcset=${srcSet}
        width=${intWidth}
        style="aspect-ratio: ${fixedAspectRatio};"
      />
    `
  }
}

window.customElements.define('dna-fixed-width-image', DNAFixedWidthImage)

export class DNAFluidImage extends LitElement {
  static get properties() {
    return {
      aspectRatio: { type: String },
      crop: { type: String },
      decoding: { type: String },
      imageWidths: { type: Array },
      loader: { type: Object, attribute: false },
      loading: { type: String },
      fetchPriority: { type: String, attribute: 'fetch-priority' },
      normalizedProps: { type: Object },
      placeholderWidth: { type: Number },
      sizes: { type: String },
    }
  }

  constructor() {
    super()
    this.aspectRatio = undefined
    this.crop = false
    this.decoding = 'async'
    this.imageWidths = []
    this.loader = undefined
    this.loading = 'lazy'
    this.fetchPriority = 'low'
    this.normalizedProps = {}
    this.placeholderWidth = 100
    this.sizes = undefined
  }

  render() {
    const {
      aspectRatio,
      crop,
      decoding,
      imageWidths,
      loader,
      loading,
      fetchPriority,
      normalizedProps,
      placeholderWidth,
      sizes,
    } = this

    const sizesArray =
      imageWidths === undefined
        ? undefined
        : generateSizes(imageWidths, aspectRatio, crop)

    const parsedAspectRatio = parseAspectRatio(aspectRatio)

    const placeholderHeight =
      aspectRatio && placeholderWidth && !Number.isNaN(parsedAspectRatio)
        ? placeholderWidth * (parsedAspectRatio ?? 1)
        : undefined

    const srcSet = generateSrcSet(normalizedProps.src, sizesArray, loader)
    const src = loader({
      src: normalizedProps.src,
      width: placeholderWidth,
      height: placeholderHeight,
      crop: crop,
    })

    return html`
      <img
        alt=${normalizedProps.alt}
        decoding=${decoding}
        height=${ifDefined(placeholderHeight)}
        loading=${loading}
        fetchpriority=${fetchPriority}
        sizes=${sizes}
        srcset=${srcSet}
        src=${src}
        width=${placeholderWidth}
        style="width: ${normalizedProps.width};${aspectRatio ? ` aspect-ratio: ${aspectRatio};` : ''}"
      />
    `
  }
}

window.customElements.define('dna-fluid-image', DNAFluidImage)

/**
 * Generates an array of image widths based on the provided parameters.
 * @param {string} width - The base width of the image.
 * @param {number} intervals - The number of intervals to generate.
 * @param {number} startingWidth - The starting width of the intervals.
 * @param {number} incrementSize - The size of each interval increment.
 * @returns {number[]} An array of image widths.
 */
export function generateImageWidths(
  width,
  intervals,
  startingWidth,
  incrementSize,
) {
  if (startingWidth === null || startingWidth === undefined) {
    return undefined
  }

  const imageWidths = []
  let currentWidth = startingWidth

  for (let i = 0; i < intervals; i++) {
    imageWidths.push(currentWidth)
    currentWidth += incrementSize
  }

  return imageWidths
}

/**
 * Generates an array of sizes for the image based on the provided widths and aspect ratio.
 * @param {number[]} widths - An array of image widths.
 * @param {string} aspectRatio - The aspect ratio of the image.
 * @param {string} crop - The crop style of the image.
 * @returns {string[]} An array of sizes for the image.
 */
export function generateSizes(widths, aspectRatio, crop) {
  if (!widths) {
    return undefined
  }

  return widths.map((width) => {
    const height = aspectRatio
      ? Math.floor(width / parseAspectRatio(aspectRatio))
      : undefined
    return `${width}x${height ? height : ''}${crop ? `_crop_${crop}` : ''}`
  })
}

/**
 * Generates a srcSet string based on the provided image source, sizes, and loader function.
 * @param {string} src - The source URL of the image.
 * @param {string[]} sizes - An array of sizes for the image.
 * @param {Function} loader - A function that generates the URL for each size.
 * @returns {string} The generated srcSet string.
 */
export function generateSrcSet(src, sizes, loader) {
  if (!src || !sizes || !loader) {
    return undefined
  }
  return sizes
    .map((size) => {
      const [width, height] = size.split('x')
      const url = loader({
        src,
        width: Number.parseInt(width, 10),
        height: Number.parseInt(height, 10),
      })
      return `${url} ${width}w`
    })
    .join(', ')
}

/**
 * Parses an aspect ratio string and returns the numeric value.
 * @param {string} aspectRatio - The aspect ratio string (e.g., "4/3").
 * @returns {number} The numeric value of the aspect ratio.
 */
export function parseAspectRatio(aspectRatio) {
  if (!aspectRatio) {
    return undefined
  }

  const [width, height] = aspectRatio.split('/')
  return Number.parseInt(width, 10) / Number.parseInt(height, 10)
}

/**
 * Checks if two values have matching units.
 * @param {string|number} a - The first value.
 * @param {string|number} b - The second value.
 * @returns {boolean} Whether the values have matching units.
 */
export function unitsMatch(a, b) {
  if (!a || !b) {
    return false
  }

  const unitsRegex = /[a-z]+$/i
  const aUnit = a.toString().match(unitsRegex)
  const bUnit = b.toString().match(unitsRegex)

  return aUnit && bUnit && aUnit[0].toLowerCase() === bUnit[0].toLowerCase()
}

/**
 * Checks if a given width represents a fixed width.
 * @param {string} width - The width value.
 * @returns {boolean} Whether the width represents a fixed width.
 */
export function isFixedWidth(width) {
  if (!width) {
    return false
  }

  const fixedWidthRegex = /^\d+(?:px)?$/i
  return fixedWidthRegex.test(width.toString())
}

/**
 * Extracts the numeric value and unit from a string.
 * @param {string} value - The input value.
 * @returns {{number: number, unit: string}} An object containing the numeric value and unit.
 */
export function getUnitValueParts(value) {
  const valueString = value.toString()
  const number = Number.parseFloat(valueString)
  const unit = valueString.replace(String(number), '')
  return { number, unit }
}

/**
 * Returns the normalized fixed unit from a value.
 * @param {string|number} value - The input value.
 * @returns {number} The normalized fixed unit.
 */
export function getNormalizedFixedUnit(value) {
  if (typeof value === 'number') {
    return value
  }

  const parts = getUnitValueParts(value)
  return parts.unit === 'px' ? parts.number : undefined
}

/**
 * A default loader function that generates URLs for different image sizes.
 * @param {Object} options - The options for generating the URL.
 * @param {string} options.src - The source URL of the image.
 * @param {number} options.width - The width of the image.
 * @param {number} options.height - The height of the image.
 * @param {string} options.crop - The crop style of the image.
 * @returns {string} The generated URL for the image.
 */
// export function shopifyLoader({ src, width, height, crop }) {
//   const size = width ? `${width}x${height ? height : ''}` : undefined
//   const cropParam = crop ? `_crop_${crop}` : ''
//   return `${src}${size ? `_${size}` : ''}${cropParam}`
// }

/**
 * The shopifyLoader function is a simple utility function that takes a src, width,
 * height, and crop and returns a string that can be used as the src for an image.
 * It can be used with the Hydrogen Image component or with the next/image component.
 * (or any others that accept equivalent configuration)
 * @param {Object} options - The options for the shopifyLoader function.
 * @param {string} options.src - The source URL of the image, e.g. `https://cdn.shopify.com/static/sample-images/garnished.jpeg`
 * @param {number} options.width - The width of the image, e.g. `100`
 * @param {number} options.height - The height of the image, e.g. `100`
 * @param {string} options.crop - The crop of the image, e.g. `center`
 * @returns {string} A Shopify image URL with the correct query parameters, e.g. `https://cdn.shopify.com/static/sample-images/garnished.jpeg?width=100&height=100&crop=center`
 *
 * @example
 * ```
 * shopifyLoader({
 *   src: 'https://cdn.shopify.com/static/sample-images/garnished.jpeg',
 *   width: 100,
 *   height: 100,
 *   crop: 'center',
 * })
 * ```
 */
export function shopifyLoader({ src, width, height, crop }) {
  if (!src) {
    return ''
  }
  const hasProtocol = /^https?:\/\//i.test(src)
  const isProtoRelative = /^\/\//i.test(src)
  const urlWithProtocol = hasProtocol
    ? src
    : isProtoRelative
      ? `https:${src}`
      : `https://${window.location.host}/${src.replace(/^\/+/g, '')}`
  const url = new URL(urlWithProtocol)
  if (width) {
    url.searchParams.append('width', Math.round(width).toString())
  }
  if (height) {
    url.searchParams.append('height', Math.round(height).toString())
  }
  if (crop) {
    url.searchParams.append('crop', crop)
  }
  return hasProtocol ? url.href : url.href.replace(/^https:/, '')
}
